import { NumberDate } from '@lemon/core/model/common'
import { CurrencyService } from '@lemon/core/service/currency.service'
import { DateService } from '@lemon/core/service/date.service'
import { EuVatSpecMap, LaskunAlv, LaskunTyypit, LaskunTyyppi } from '../model/lasku'
import { LaskuKopioija } from './lasku/lasku.kopioija'

export interface EuVatSpecMapProvider {
  provide(): Promise<EuVatSpecMap>
}

export interface AlvService {
  annaLaskutyypinAlvt(laskunTyyppi: LaskunTyyppi, maaKoodi: string): Promise<LaskunAlv[]>
}

export abstract class AlvBaseService implements AlvService {
  constructor(
    protected _currencyService: CurrencyService,
    protected _dateService: DateService,
  ) {}

  private onkoAlvVoimassa(alv: LaskunAlv, date: NumberDate): boolean {
    if (!alv.s && !alv.e) {
      // Voimassa ajan alusta ajan loppuun
      return true
    } else if (alv.e && alv.s) {
      // Voimassa tietyn aikavälin
      return this._dateService.compareNumberDates(alv.s, '<=', date) && this._dateService.compareNumberDates(date, '<=', alv.e)
    } else if (alv.e) {
      // Voimassa tiettyyn päivämäärään saakka
      return this._dateService.compareNumberDates(date, '<=', alv.e)
    } else if (alv.s) {
      // Voimassa tietystä päivämäärästä saakka
      return this._dateService.compareNumberDates(alv.s, '<=', date)
    }
    throw new Error('Impossible')
  }

  public annaDefaultAlv(alvt: LaskunAlv[], date: NumberDate): LaskunAlv {
    for (const alv of alvt) {
      if (alv.d && this.onkoAlvVoimassa(alv, date)) {
        return alv
      }
    }
    return null
  }

  public abstract annaLaskutyypinAlvt(laskunTyyppi: LaskunTyyppi, maaKoodi: string): Promise<LaskunAlv[]>

  public async annaLaskutyypinAlvtWithSpecMapProvider(laskunTyyppi: LaskunTyyppi, maaKoodi: string, euVatSpecMapProvider: EuVatSpecMapProvider): Promise<LaskunAlv[]> {
    if (laskunTyyppi.avain === LaskunTyypit.TAVALLINEN.avain || laskunTyyppi.avain === LaskunTyypit.TAVALLINEN_ULKOM.avain) {
      return Promise.resolve<LaskunAlv[]>([
        { prosentti: 0, tunniste: 'suomi0' },
        { prosentti: 10, tunniste: 'suomi10' },
        { prosentti: 13.5, tunniste: 'suomi135', s: this._dateService.lisaaPaiviaNumber(LaskuKopioija.suomi14To135CutoffDate, 1) },
        { prosentti: 14, tunniste: 'suomi14', e: LaskuKopioija.suomi14To135CutoffDate },
        { prosentti: 24, tunniste: 'suomi24', d: 1, e: LaskuKopioija.suomi24To255CutoffDate },
        { prosentti: 25.5, tunniste: 'suomi255', d: 1, s: this._dateService.lisaaPaiviaNumber(LaskuKopioija.suomi24To255CutoffDate, 1) },
      ])
    } else if (laskunTyyppi.avain === LaskunTyypit.EU_TAVARA.avain) {
      return Promise.resolve<LaskunAlv[]>([{ prosentti: 0, tunniste: 'yht', d: 1 }])
    } else if (laskunTyyppi.avain === LaskunTyypit.EU_PALVELU.avain) {
      return Promise.resolve<LaskunAlv[]>([{ prosentti: 0, tunniste: 'yht', d: 1 }])
    } else if (laskunTyyppi.avain === LaskunTyypit.EU_KULUTTAJA.avain || laskunTyyppi.avain === LaskunTyypit.EU_KULUTTAJA_EI_REKISTEROITYNYT.avain) {
      return Promise.resolve<LaskunAlv[]>([
        { prosentti: 0, tunniste: 'suomi0' },
        { prosentti: 10, tunniste: 'suomi10' },
        { prosentti: 13.5, tunniste: 'suomi135', s: this._dateService.lisaaPaiviaNumber(LaskuKopioija.suomi14To135CutoffDate, 1) },
        { prosentti: 14, tunniste: 'suomi14', e: LaskuKopioija.suomi14To135CutoffDate },
        { prosentti: 24, tunniste: 'suomi24', d: 1, e: LaskuKopioija.suomi24To255CutoffDate },
        { prosentti: 25.5, tunniste: 'suomi255', d: 1, s: this._dateService.lisaaPaiviaNumber(LaskuKopioija.suomi24To255CutoffDate, 1) },
      ])
    } else if (laskunTyyppi.avain === LaskunTyypit.EU_KULUTTAJA_PALVELU.avain || laskunTyyppi.avain === LaskunTyypit.EU_KULUTTAJA_TAVARA.avain) {
      if (!maaKoodi) {
        return []
      }
      const alvMap = await euVatSpecMapProvider.provide()
      const alvtForCountry = alvMap[maaKoodi]
      if (!alvtForCountry?.length) {
        console.error('No rates found for ' + maaKoodi)
        throw new Error('No rates found for ' + maaKoodi)
      }
      // console.log('alvtForCountry', alvtForCountry)
      const alvt: LaskunAlv[] = alvtForCountry.map(rate => {
        const ret: LaskunAlv = {
          prosentti: rate.p,
          tunniste: this._annaEtamyyntiTunniste(maaKoodi, rate.p),
        }
        if (rate.d) {
          ret.d = 1
        }
        return ret
      })
      return Promise.resolve<LaskunAlv[]>(alvt)
    } else if (laskunTyyppi.avain === LaskunTyypit.RAKENNUSALA.avain) {
      return Promise.resolve<LaskunAlv[]>([{ prosentti: 0, tunniste: 'rak', d: 1 }])
    } else if (laskunTyyppi.avain === LaskunTyypit.MUU_MAAILMA.avain) {
      return Promise.resolve<LaskunAlv[]>([{ prosentti: 0, tunniste: 'eieu', d: 1 }])
    }
    throw new Error('Unknown laskun tyyppi ' + laskunTyyppi.avain)
  }

  protected _annaEtamyyntiTunniste(maaKoodi: string, prosentti: number) {
    return 'k_' + maaKoodi.toLowerCase() + '_' + this._currencyService.formatoiDesimaaliSailytaNollat(prosentti, 2, 'en')
  }

  puraEtamyyntiTunnisteTaiNull(tunniste: string): { maakoodi: string; prosentti: number } | null {
    if (tunniste?.startsWith('k_')) {
      const maakoodi = tunniste.substr(2, 3).toUpperCase()
      const prosenttiString = tunniste.substr(tunniste.lastIndexOf('_') + 1)
      return {
        maakoodi: maakoodi,
        prosentti: this._currencyService.muutaMerkkijonoNumeroksi(prosenttiString, 2),
      }
    }
    return null
  }

  puraEtamyyntiTunniste(tunniste: string): { maakoodi: string; prosentti: number } {
    if (!tunniste?.startsWith('k_')) {
      throw new Error('Annettu tunniste "' + tunniste + '" ei ole etämyyntitunniste!')
    }
    const maakoodi = tunniste.substr(2, 3).toUpperCase()
    const prosenttiString = tunniste.substr(tunniste.lastIndexOf('_') + 1)
    return {
      maakoodi: maakoodi,
      prosentti: this._currencyService.muutaMerkkijonoNumeroksi(prosenttiString, 2),
    }
  }
}
